"use client";

import { useMemo } from "react";
import useSWR from "swr";
import getUserContextFromClient from "@/lib/user-context/getUserContextFromClient";

interface OfferingsSearchResponse {
  pagination?: {
    totalCount?: number;
  };
}

const SEARCH_URL =
  "https://offerings.api.highereducation.com/v1/search?visibility=search";

/**
 * Converts a URLSearchParams string into a JSON body for the offerings search API.
 * Params with multiple values are grouped into arrays.
 */
function searchParamsToBody(
  paramsString: string,
): Record<string, string | string[]> {
  const params = new URLSearchParams(paramsString);
  const body: Record<string, string | string[]> = {};

  params.forEach((value, key) => {
    const existing = body[key];
    if (existing === undefined) {
      body[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      body[key] = [existing, value];
    }
  });

  return body;
}

// Mirrors `applyGeoToUrl` in se-supersonic-ui's dynamicFetchUtils so the
// count call is geo-filtered the same way the DynamicFlow results are.
function buildSearchUrl(country: string | null): string {
  if (!country) return SEARCH_URL;
  const url = new URL(SEARCH_URL);
  if (!url.searchParams.has("country")) {
    url.searchParams.set("country", country);
  }
  return url.toString();
}

async function fetchSearchCount(
  paramsString: string,
  country: string | null,
): Promise<number> {
  const body = searchParamsToBody(paramsString);

  const response = await fetch(buildSearchUrl(country), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(
      `Offerings search failed: ${response.status} ${response.statusText}`,
    );
  }

  const data: OfferingsSearchResponse = await response.json();
  return data?.pagination?.totalCount ?? 0;
}

export function useOfferingsSearchCount(searchParams: string) {
  const { country } = useMemo(() => getUserContextFromClient(), []);
  const swrKey = searchParams
    ? `offerings-search-count:${country ?? ""}:${searchParams}`
    : null;

  const { data, error, isLoading } = useSWR<number>(
    swrKey,
    () => fetchSearchCount(searchParams, country),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      keepPreviousData: true,
    },
  );

  return {
    numberOfHits: data ?? 0,
    isLoading,
    error,
  };
}
